/**
 * Topic photography for blog posts (public/blog-art/*.jpg — see the
 * CREDITS.md there for sources and licence).
 *
 * Every published post is pinned to its own photo in PINNED, chosen for
 * that post's actual angle rather than its broad topic, so no two posts
 * share an image. A post's own `hero_image` still wins over the pin.
 *
 * Posts that are not pinned yet — anything the daily routine writes
 * before someone picks a picture for it — fall back to the tag-matched
 * topic pool below, so a new post always renders with something.
 */
const PINNED = {
  'first-three-months-new-dad': ['newborn', 'A father holding his baby in his arms'],
  'engaged-father-modern-parenting': ['fatherhood', 'A father outdoors with his three children'],
  'work-life-balance-for-fathers': ['work-life', 'A father helping his daughter with schoolwork at home'],
  'listen-better-to-your-partner': ['listening', 'A man listening to his partner on the sofa'],
  'better-communication-with-partner': ['communication', 'A couple talking in a hallway'],
  'emotional-intelligence-for-men-relationships': ['growth', 'A man in thought, looking away'],
  'identity-based-habits-better-husband': ['growth-2', 'A pensive man at a window'],
  'self-improvement-relationship-balance': ['presence-2', 'A couple making coffee together in the morning'],
  'daily-presence-relationship-tips': ['presence', 'A couple with coffee in the kitchen'],
  'keeping-the-spark-in-long-term-relationships': ['spark', 'A couple close together, foreheads touching'],
  'small-daily-gestures-marriage-strength': ['spark-2', 'A close embrace, arms around the waist'],
  'sharing-the-mental-load-at-home': ['mental-load', 'A man washing dishes while his partner is on her phone'],
  'habit-stacking-relationship-routine': ['mental-load-2', 'A couple tidying the living room together'],
  'tiny-daily-habits-stronger-marriage': ['habits', 'A couple walking hand in hand past a wall'],
  '1-percent-rule-marriage': ['habits-2', 'A couple walking hand in hand through a field'],
  'build-relationship-systems-not-goals': ['systems', 'A couple planning together at a table'],
  'welcome-to-better-husband-blog': ['welcome', 'A couple smiling together in a field'],
};

/* Fallback pool for posts that have not been pinned yet. Match order
   matters — more specific topics come before broad ones. */
const TOPICS = [
  { art: 'communication', variants: 2, match: /communicat|listen/ },
  { art: 'mental-load', variants: 2, match: /mental.load|household|home management/ },
  { art: 'fatherhood', variants: 2, match: /father|parent|family time/ },
  { art: 'spark', variants: 2, match: /spark|long.term|maintenance/ },
  { art: 'presence', variants: 2, match: /presence|work-life|balance/ },
  { art: 'growth', variants: 2, match: /self.improvement|personal growth|emotional|identity|individuality/ },
  { art: 'habits', variants: 2, match: /habit|percent|system|goal|gesture|daily/ },
];

const FALLBACK = 'welcome';

const topicFor = (post) => {
  const haystack = [...(post?.tags || []), post?.slug || ''].join(' ').toLowerCase();
  return TOPICS.find((t) => t.match.test(haystack)) || null;
};

/**
 * Unpinned posts cluster by topic, so several in a row would otherwise
 * show the same photo. Assign each topic's variants round-robin down the
 * list the blog actually renders (newest first), which makes neighbours
 * in the grid land on different photos. Computed once at module load; a
 * given slug always resolves to the same photo.
 */
const buildAssignments = () => {
  const bySlug = {};
  let posts = [];
  try {
    const ctx = require.context('../content/posts', false, /\.json$/);
    posts = ctx.keys().map((k) => ctx(k));
  } catch (e) {
    return bySlug; // non-webpack consumer (e.g. a unit test): fall back per-post
  }
  const usedPerTopic = {};
  posts
    .filter((p) => p && p.slug && !PINNED[p.slug])
    .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))
    .forEach((post) => {
      const topic = topicFor(post);
      if (!topic) {
        bySlug[post.slug] = FALLBACK;
        return;
      }
      const nth = (usedPerTopic[topic.art] || 0) % topic.variants;
      usedPerTopic[topic.art] = (usedPerTopic[topic.art] || 0) + 1;
      bySlug[post.slug] = nth === 0 ? topic.art : `${topic.art}-${nth + 1}`;
    });
  return bySlug;
};

const ASSIGNMENTS = buildAssignments();

export function blogArtFor(post) {
  if (post?.hero_image?.url) {
    return { url: post.hero_image.url, alt: post.hero_image.alt || '' };
  }

  const pin = PINNED[post?.slug];
  if (pin) {
    return { url: `/blog-art/${pin[0]}.jpg`, alt: pin[1] };
  }

  const assigned = ASSIGNMENTS[post?.slug];
  const name = assigned || (topicFor(post) || { art: FALLBACK }).art;
  return { url: `/blog-art/${name}.jpg`, alt: '' };
}
